import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  User,
  FileText,
  PenTool,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const Visitor = () => {
  const { visitId } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    purpose: "",
    driveIn: false,
    vehicleNumber: "",
    meetingLocation: "",
  });
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  // Fetch visitor data on component mount
  useEffect(() => {
    const fetchVisitorData = async () => {
      if (!visitId) {
        console.log("No visitId found");
        return;
      }

      console.log("Fetching data for visitId:", visitId);

      try {
        setIsLoading(true);
        const response = await fetch(
          `https://njs-01.optimuslab.space/api/visits/${visitId}`
        );

        console.log("Response status:", response.status);

        if (response.ok) {
          const visitData = await response.json();
          console.log("Fetched visit data:", visitData);

          // Pre-fill form with visitor data
          setFormData({
            name: visitData.visitor?.Name || "",
            email: visitData.visitor?.email || "",
            company: visitData.visitor?.company || "",
            purpose: visitData.purpose || "",
            driveIn: false,
            vehicleNumber: "",
            meetingLocation: visitData.meetingLocation || "",
          });
          console.log("Form data set");
        } else {
          console.error("Failed to fetch visitor data:", response.status);
        }
      } catch (error) {
        console.error("Error fetching visitor data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVisitorData();
  }, [visitId]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");

    isDrawingRef.current = true;
    setIsDrawing(true);

    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");

    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);

    // Save signature data
    const canvas = canvasRef.current;
    setSignatureData(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleSubmit = async () => {
    if (!agreementAccepted) {
      alert("Please accept the NDA agreement to continue.");
      return;
    }

    if (!signatureData) {
      alert("Please provide your signature to complete the registration.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const signatureBlob = await fetch(signatureData).then((r) => r.blob());
      const formDataToSend = new FormData();

      formDataToSend.append("name", formData.name);
      formDataToSend.append("company", formData.company);
      formDataToSend.append("purpose", formData.purpose);
      formDataToSend.append("hostEmployeeId", "");
      formDataToSend.append("meetingLocation", formData.meetingLocation); 
      formDataToSend.append("signatureFile", signatureBlob, "signature.png");

      // ADD THIS LINE
      if (formData.driveIn && formData.vehicleNumber) {
        formDataToSend.append("vehicleNumber", formData.vehicleNumber);
      }

      console.log("Submitting to visitId:", visitId);

      const signatureResponse = await fetch(
        `https://njs-01.optimuslab.space/api/visits/${visitId}/complete`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      console.log("Response status:", signatureResponse.status);

      if (signatureResponse.ok) {
        const result = await signatureResponse.json();
        console.log("Success response:", result);
        setSubmitStatus("success");
      } else {
        const errorText = await signatureResponse.text();
        console.error("Error response:", errorText);
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p>Loading visitor information...</p>
      </div>
    );
  }

  if (!visitId) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p className="text-red-600">
          Invalid visit link. Please check your email for the correct link.
        </p>
      </div>
    );
  }

  if (submitStatus === "success") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="max-w-2xl p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Registration Complete!
            </h2>
            <p className="text-gray-600">
              Pre-Registration Complete! You will receive a confirmation email
              with your QR code once approved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl lg:max-w-7xl mx-auto p-6 ">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Visitor Pre-Registration
        </h1>
        <p className="text-gray-600">
          Please complete all sections to register for your visit
        </p>
      </div>

      {/* Main Form Layout - Two Column for Large Screens */}
      {/* Step-based Form Layout */}
      <div className="bg-white rounded-lg shadow-lg">
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Personal Information
                </h2>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium">
                      Important Verification Notice
                    </p>
                    <p className="text-blue-700 text-sm mt-1">
  Please ensure your full name exactly matches that on your 
  government-issued ID. If it doesn't match, you can edit it below.
</p>
                  </div>
                </div>
              </div>

              <div className="max-w-2xl mx-auto">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address 
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose of Visit *
                  </label>
                  <textarea
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief description of your visit purpose..."
                    required
                  />
                </div>
                <div className="mt-4">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Meeting Location
  </label>
  <input
    type="text"
    value={formData.meetingLocation}
    readOnly
    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
  />
</div>
                <div className="mt-6">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      name="driveIn"
                      checked={formData.driveIn}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          driveIn: e.target.checked,
                          vehicleNumber: e.target.checked
                            ? formData.vehicleNumber
                            : "",
                        })
                      }
                      className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    Drive-in to the venue?
                  </label>
                </div>

                {formData.driveIn && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle License Number *
                    </label>
                    <input
                      type="text"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your vehicle license number"
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: NDA Agreement */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Non-Disclosure Agreement
                </h2>
              </div>

              <div className="bg-white border rounded-lg p-4 max-h-72 overflow-y-auto mb-4">
                <div className="text-sm text-gray-700 space-y-3">
                  <p className="font-semibold">
                    CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT
                  </p>

                  <p>
                    By signing this agreement, you acknowledge and agree to the
                    following terms and conditions regarding your visit to our
                    premises:
                  </p>

                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>
                      <strong>Confidential Information:</strong> You may be
                      exposed to confidential, proprietary, or sensitive
                      information during your visit. This includes but is not
                      limited to business strategies, technical data, financial
                      information, and personnel matters.
                    </li>
                    <li>
                      <strong>Non-Disclosure:</strong> You agree not to
                      disclose, share, or use any confidential information
                      obtained during your visit for any purpose other than the
                      intended business purpose.
                    </li>
                    <li>
                      <strong>Security Compliance:</strong> You agree to follow
                      all security protocols and guidelines provided during your
                      visit, including badge requirements and restricted area
                      policies.
                    </li>

                    <li>
                      <strong>Compliance:</strong> You agree to comply with all
                      company policies and applicable laws during your visit.
                    </li>
                  </ol>

                  <p>
                    This agreement remains in effect indefinitely and survives
                    the termination of your visit. Violation of this agreement
                    may result in legal action.
                  </p>
                </div>
              </div>

              <div className="flex items-start mb-6">
                <input
                  type="checkbox"
                  id="nda-agreement"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  className="mt-1 mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="nda-agreement"
                  className="text-sm text-gray-700"
                >
                  I have read, understood, and agree to the terms of this
                  Non-Disclosure Agreement. E-signing this agreement confirms my
                  acceptance and commitment to comply with all stated terms.
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: E-Signature */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <PenTool className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Electronic Signature
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Please sign below to complete your registration. Your signature
                will be securely stored.
              </p>

              <div className="border-2 border-gray-300 border-dashed rounded-lg p-4 bg-white max-w-2xl mx-auto">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={200}
                  className="w-full h-48 border border-gray-200 rounded cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />

                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-gray-500">
                    Sign above using your mouse or touch device
                  </p>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear Signature
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Full Width */}
      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-8">
        {currentStep > 1 && (
          <button
            type="button"
            onClick={() => setCurrentStep(currentStep - 1)}
            className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Previous
          </button>
        )}

        <div className="flex-1"></div>

        {currentStep < 3 ? (
          <button
            type="button"
            onClick={() => {
              if (currentStep === 1) {
                if (
                  !formData.name ||
                  !formData.email ||
                  !formData.purpose
                ) {
                  alert("Please fill in all required fields.");
                  return;
                }
                if (formData.driveIn && !formData.vehicleNumber) {
                  alert("Please enter your vehicle license number.");
                  return;
                }
              }
              if (currentStep === 2 && !agreementAccepted) {
                alert("Please accept the NDA agreement to continue.");
                return;
              }
              setCurrentStep(currentStep + 1);
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !signatureData}
            className={`px-8 py-3 rounded-md font-medium transition-colors ${
              isSubmitting || !signatureData
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
          >
            {isSubmitting ? "Submitting..." : "Complete Registration"}
          </button>
        )}
      </div>

      {submitStatus === "error" && (
        <p className="text-red-600 text-sm mt-2 text-center">
          An error occurred. Please try again.
        </p>
      )}
    </div>
  );
};

export default Visitor;
